import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import { DialogueIcon } from '@treams/treams-icons';
import {
	Competency,
	Goal,
	JobPosition,
	LocalizedString,
	useFeedbackComposerCompetenciesQuery,
} from '@treams/treams-network';
import {
	Accordion,
	Button,
	Empty,
	Label,
	Paragraph,
	Separator,
	Skeleton,
	Spinner,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { useJobTitleName } from '../../../../../job/useJobTitleName';
import FeedbackComposerCompetencyCard from './FeedbackComposerCompetencyCard';

export type FeedbackCompetencySelection = {
	competencyID: string;
	jobTitleID?: string;
	text?: string;
};

type CompetencySelectorItem = [string, Competency[], string?];

function jobPositionTitle(
	position: JobPosition,
	localize: (text: LocalizedString) => { text: string },
	jobTitleName: ReturnType<typeof useJobTitleName>,
): string {
	const jobLevel = localize(position.jobLevel?.title ?? [])?.text;
	return `${jobTitleName(position.jobTitle)} ${position.jobLevel ? `- ${jobLevel}` : ''}`;
}

function competencySelectorItemsFromJobPositions(
	jobPositions: JobPosition[],
	localize: (text: LocalizedString) => { text: string },
	jobTitleName: ReturnType<typeof useJobTitleName>,
): CompetencySelectorItem[] {
	return sortBy(
		jobPositions.map((position) => [
			jobPositionTitle(position, localize, jobTitleName),
			position.competencies.map(({ competency }) => competency),
			position.jobTitle.excludedFromGrowthPath ? undefined : position.jobTitle.id,
		]),
		([title]) => title,
	);
}

function competencySelectorItemsFromGoals(
	goals: Goal[],
	localize: (text: LocalizedString) => { text: string },
): CompetencySelectorItem[] {
	return goals.reduce((acc, goal) => {
		if (!goal.competency?.competency) {
			return acc;
		}

		return [...acc, [localize(goal.title).text, [goal.competency.competency]]];
	}, []);
}

function toggleSelectedCompetency(
	competencies: FeedbackCompetencySelection[],
	competency: Competency,
	selected: boolean,
	jobTitleID?: string,
) {
	if (selected) {
		return [...competencies, { competencyID: competency.id, jobTitleID }];
	}

	return competencies.filter((c) => c.competencyID !== competency.id);
}
function FeedbackComposerJobCompetenciesList(props: {
	selectedCompetencies: FeedbackCompetencySelection[];
	selectableCompetencies: Competency[];
	onChange: (competency: Competency, selected: boolean) => void;
}) {
	return (
		<Box
			style={{
				display: 'grid',
				gridTemplateColumns: '1fr 1fr',
				gap: tokens.gap.list,
			}}
		>
			{props.selectableCompetencies.map((competency) => {
				const isSelected = props.selectedCompetencies.find(({ competencyID }) => {
					return competency.id === competencyID;
				});

				return (
					<FeedbackComposerCompetencyCard
						key={competency.id}
						competency={competency}
						selected={Boolean(isSelected)}
						onChange={(selected) => props.onChange(competency, !selected)}
					/>
				);
			})}
		</Box>
	);
}

type CompetencyLabelWithCompetencies = {
	title?: string;
	competencies: Competency[];
};

/**
 * Group competencies by label.
 *
 * @param competencies - The competencies to group.
 * @returns A map of label IDs to their competencies.
 */
function useCompetenciesByLabel(competencies: Competency[]) {
	const { localize } = useLocalize();

	return competencies.reduce(
		(result, competency) => {
			// If the competency has a label, add it to the result
			if (competency.label) {
				const label = result[competency.label.id] ?? {
					title: localize(competency.label.title).text,
					competencies: [],
				};
				label.competencies.push(competency);
				return { ...result, [competency.label.id]: label };
			}

			// If the competency doesn't have a label, add it to the result
			const label: CompetencyLabelWithCompetencies = result.other || {
				title: undefined,
				competencies: [],
			};
			label.competencies.push(competency);
			return { ...result, other: label };
		},
		{} as Record<string, { title: string; competencies: Competency[] }>,
	);
}

function FeedbackComposerJobCompetencies(props: {
	selectedCompetencies: FeedbackCompetencySelection[];
	selectableCompetencies: Competency[];
	onChange: (competency: Competency, selected: boolean) => void;
}) {
	const labels = useCompetenciesByLabel(props.selectableCompetencies);

	return (
		<>
			{Object.values(labels)
				.sort((a, b) => {
					if (a.title && b.title) {
						return a.title.localeCompare(b.title);
					}
					return 0;
				})
				.map((label, index) => (
					<>
						<Paragraph mb={1} mt={index > 0 ? 3 : 0}>
							{label.title}
						</Paragraph>
						<FeedbackComposerJobCompetenciesList
							selectedCompetencies={props.selectedCompetencies}
							selectableCompetencies={label.competencies}
							onChange={props.onChange}
						/>
					</>
				))}
		</>
	);
}

export default function FeedbackComposerCompetencies(props: {
	userID: string;
	competencies: FeedbackCompetencySelection[];
	onChange: (competencies: FeedbackCompetencySelection[]) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const jobTitleName = useJobTitleName();

	const query = useFeedbackComposerCompetenciesQuery({
		variables: {
			userID: props.userID,
		},
	});

	const goalCompetencies = competencySelectorItemsFromGoals(
		(query.data?.user.goals as Goal[]) ?? [],
		localize,
	);
	const jobCompetencies = competencySelectorItemsFromJobPositions(
		(query.data?.user.jobPositions as JobPosition[]) ?? [],
		localize,
		jobTitleName,
	);

	const onChangeHandler = (competency: Competency, isSelected: boolean, jobTitleID?: string) => {
		props.onChange(
			toggleSelectedCompetency(props.competencies, competency, isSelected, jobTitleID),
		);
	};

	return (
		<Box>
			<Flex py={3} alignItems="center">
				<Box flexGrow={1} flexShrink={1}>
					<Label>{t('feedback.composer.competencies.title')}</Label>
				</Box>
				<Button
					small
					type="secondary"
					title={t('feedback.composer.competencies.selectAll')}
					onClick={() => {
						props.onChange(
							query.data?.user.jobPositions.flatMap((position) =>
								position.competencies.map(({ competency }) => ({
									competencyID: competency.id,
									jobTitleID: position.jobTitle.excludedFromGrowthPath
										? undefined
										: position.jobTitle.id,
								})),
							) ?? [],
						);
					}}
				/>
			</Flex>
			{query.data ? (
				<Box>
					{jobCompetencies.length === 0 && (
						<Skeleton.Empty
							mt={2}
							contents={
								<Empty
									small
									icon={<DialogueIcon />}
									title={t('feedback.composer.competencies.empty.jobPositions.title')}
								/>
							}
						>
							<Skeleton.Box height={100} />
						</Skeleton.Empty>
					)}

					{jobCompetencies.map(([title, competenciesToSelect, jobTitleID]) => (
						<Box key={title} mt={3}>
							<Subheading>{title}</Subheading>
							<FeedbackComposerJobCompetencies
								selectedCompetencies={props.competencies}
								selectableCompetencies={competenciesToSelect}
								onChange={(competency, selected) =>
									onChangeHandler(competency, selected, jobTitleID)
								}
							/>
						</Box>
					))}

					{goalCompetencies.length > 0 && (
						<>
							<Separator my={2} />
							<Accordion
								title={t('feedback.composer.competencies.goals.title')}
								initialIsCollapsed={true}
								borderless
							>
								{goalCompetencies.map(([title, competenciesToSelect]) => (
									<Box key={title} mt={3}>
										<Subheading>{title}</Subheading>
										<FeedbackComposerJobCompetencies
											selectedCompetencies={props.competencies}
											selectableCompetencies={competenciesToSelect}
											onChange={onChangeHandler}
										/>
									</Box>
								))}
							</Accordion>
						</>
					)}
				</Box>
			) : (
				<Box>
					<Spinner centered />
				</Box>
			)}
		</Box>
	);
}
